/**
 * Phase 14 — Approval-pipeline panel for the Story editor.
 *
 * Renders the workflow stages that require approval, and shows for each:
 *  - the granted / required vote count
 *  - the list of votes with timestamps and inline reject-comment
 *  - action buttons (Approve / Reject / Withdraw) for the active user
 *
 * Hooks straight into the Phase-14 SDK methods (`listApprovals`,
 * `voteOnTransition`, `withdrawApproval`) — no React Query cache
 * dependencies beyond what's already wired up in the editor.
 *
 * Mounted alongside other sidebar panels in the Story editor — the
 * panel itself decides whether to render (only shows if the Space has
 * at least one stage with `requires_approval=true`).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type JSX } from 'react';
import type { ManagementResult } from '@peacock/sdk-js';
import { makeApi } from '../../lib/api';
import { useAuthStore } from '../auth/authStore';

interface ApprovalsPanelProps {
  storyUuid: string;
}

function resultCode<T>(result: ManagementResult<T>): string {
  if (result.status === 'forbidden') return result.code;
  if (result.status === 'error') return `http-${result.code}`;
  return result.status;
}

export function ApprovalsPanel({ storyUuid }: ApprovalsPanelProps): JSX.Element | null {
  const token = useAuthStore((s) => s.token);
  const spaceSlug = useAuthStore((s) => s.spaceSlug);
  const queryClient = useQueryClient();

  const [activeStage, setActiveStage] = useState<string | null>(null);
  const [rejectMessage, setRejectMessage] = useState('');
  const [statusMessage, setStatusMessage] = useState<
    { kind: 'ok' | 'err'; text: string } | null
  >(null);

  const enabled = token !== null && spaceSlug !== null;

  const approvalsQuery = useQuery({
    enabled,
    queryKey: ['approvals', spaceSlug, storyUuid],
    queryFn: async () => {
      if (token === null || spaceSlug === null) {
        throw new Error('no-auth');
      }
      const api = makeApi(token);
      const result = await api.listApprovals(spaceSlug, storyUuid);
      if (result.status !== 'ok') {
        throw new Error(resultCode(result));
      }
      return result.data;
    },
  });

  const voteMutation = useMutation({
    mutationFn: async (input: {
      stage: string;
      vote: 'approve' | 'reject';
      message?: string;
    }) => {
      if (token === null || spaceSlug === null) {
        throw new Error('no-auth');
      }
      const api = makeApi(token);
      const result = await api.voteOnTransition(
        spaceSlug,
        storyUuid,
        input.stage,
        input.vote,
        input.message,
      );
      if (result.status !== 'ok') {
        throw new Error(resultCode(result));
      }
      return result.data;
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ['approvals', spaceSlug, storyUuid] });
      setStatusMessage({
        kind: 'ok',
        text: vars.vote === 'approve'
          ? `Zustimmung für "${vars.stage}" eingetragen.`
          : `Ablehnung für "${vars.stage}" eingetragen.`,
      });
      setActiveStage(null);
      setRejectMessage('');
    },
    onError: (e: Error) => {
      setStatusMessage({ kind: 'err', text: `Fehler: ${e.message}` });
    },
  });

  const withdrawMutation = useMutation({
    mutationFn: async (stage: string) => {
      if (token === null || spaceSlug === null) {
        throw new Error('no-auth');
      }
      const api = makeApi(token);
      const result = await api.withdrawApproval(spaceSlug, storyUuid, stage);
      if (result.status !== 'ok') {
        throw new Error(resultCode(result));
      }
      return result.data;
    },
    onSuccess: (_data, stage) => {
      void queryClient.invalidateQueries({ queryKey: ['approvals', spaceSlug, storyUuid] });
      setStatusMessage({ kind: 'ok', text: `Stimme für "${stage}" zurückgezogen.` });
    },
    onError: (e: Error) => {
      setStatusMessage({ kind: 'err', text: `Fehler: ${e.message}` });
    },
  });

  if (!enabled) {
    return null;
  }
  if (approvalsQuery.isLoading) {
    return (
      <aside
        data-testid="approvals-panel-loading"
        className="rounded-md border border-zinc-200 bg-white p-3 text-xs text-zinc-500"
      >
        Approvals werden geladen…
      </aside>
    );
  }
  if (approvalsQuery.isError || !approvalsQuery.data) {
    return null;
  }

  const stages = approvalsQuery.data.data;
  const approvalStages = stages.filter((s) => s.stage.requires_approval);
  if (approvalStages.length === 0) {
    // No stage in this Space requires approval — nothing to render.
    return null;
  }

  return (
    <aside
      data-testid="approvals-panel"
      className="rounded-md border border-zinc-200 bg-white p-3"
    >
      <header className="mb-2 flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-700">
          Approval-Pipeline
        </h3>
        <span className="text-[10px] text-zinc-400">Phase 14</span>
      </header>

      {statusMessage ? (
        <p
          className={
            'mb-2 rounded px-2 py-1 text-xs ' +
            (statusMessage.kind === 'ok'
              ? 'bg-emerald-50 text-emerald-800'
              : 'bg-rose-50 text-rose-800')
          }
          role="status"
        >
          {statusMessage.text}
        </p>
      ) : null}

      <ul className="space-y-2">
        {approvalStages.map((status) => {
          const stage = status.stage;
          const gateColor = status.is_unblocked
            ? 'bg-emerald-100 text-emerald-800'
            : status.rejected_count > 0
              ? 'bg-rose-100 text-rose-800'
              : 'bg-amber-100 text-amber-800';
          return (
            <li
              key={stage.id}
              data-testid={`approval-stage-${stage.name}`}
              className="rounded border border-zinc-100 p-2 text-xs"
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-zinc-900">{stage.label}</span>
                <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${gateColor}`}>
                  {status.granted_count} / {stage.required_approvals}
                  {status.rejected_count > 0 ? ` · ${status.rejected_count}× ✕` : ''}
                </span>
              </div>

              {status.votes.length > 0 ? (
                <ul className="mt-1 space-y-0.5 text-[11px] text-zinc-600">
                  {status.votes.map((v) => (
                    <li key={v.id} className="flex items-center gap-1">
                      <span
                        className={
                          'inline-block h-1.5 w-1.5 rounded-full ' +
                          (v.vote === 'approve' ? 'bg-emerald-500' : 'bg-rose-500')
                        }
                        aria-hidden
                      />
                      Token {v.actor_token_id ?? '?'} ·{' '}
                      {v.vote === 'approve' ? 'Genehmigt' : 'Abgelehnt'}
                      {v.message ? ` — "${v.message}"` : ''}
                    </li>
                  ))}
                </ul>
              ) : null}

              <div className="mt-2 flex flex-wrap items-center gap-1">
                <button
                  type="button"
                  data-testid={`approve-${stage.name}`}
                  onClick={() =>
                    voteMutation.mutate({ stage: stage.name, vote: 'approve' })
                  }
                  disabled={voteMutation.isPending}
                  className="rounded bg-emerald-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  Genehmigen
                </button>
                <button
                  type="button"
                  data-testid={`reject-toggle-${stage.name}`}
                  onClick={() => {
                    setActiveStage(activeStage === stage.name ? null : stage.name);
                    setRejectMessage('');
                  }}
                  className="rounded bg-rose-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-rose-700"
                >
                  Ablehnen…
                </button>
                <button
                  type="button"
                  data-testid={`withdraw-${stage.name}`}
                  onClick={() => withdrawMutation.mutate(stage.name)}
                  disabled={withdrawMutation.isPending}
                  className="rounded border border-zinc-300 px-2 py-1 text-[11px] text-zinc-700 hover:bg-zinc-100 disabled:opacity-50"
                >
                  Stimme zurückziehen
                </button>
              </div>

              {activeStage === stage.name ? (
                <div className="mt-2 rounded border border-rose-200 bg-rose-50 p-2">
                  <label
                    htmlFor={`reject-msg-${stage.name}`}
                    className="block text-[11px] font-medium text-rose-900"
                  >
                    Begründung für Ablehnung (optional, max 280):
                  </label>
                  <textarea
                    id={`reject-msg-${stage.name}`}
                    data-testid={`reject-message-${stage.name}`}
                    rows={2}
                    maxLength={280}
                    value={rejectMessage}
                    onChange={(e) => setRejectMessage(e.target.value)}
                    className="mt-1 w-full rounded border border-rose-300 bg-white p-1 text-xs"
                  />
                  <div className="mt-1 flex justify-end gap-1">
                    <button
                      type="button"
                      onClick={() => {
                        setActiveStage(null);
                        setRejectMessage('');
                      }}
                      className="rounded px-2 py-1 text-[11px] text-zinc-600 hover:bg-zinc-100"
                    >
                      Abbrechen
                    </button>
                    <button
                      type="button"
                      data-testid={`reject-submit-${stage.name}`}
                      onClick={() =>
                        voteMutation.mutate({
                          stage: stage.name,
                          vote: 'reject',
                          message: rejectMessage.trim() || undefined,
                        })
                      }
                      disabled={voteMutation.isPending}
                      className="rounded bg-rose-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-rose-700 disabled:opacity-50"
                    >
                      Ablehnung senden
                    </button>
                  </div>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>

      <p className="mt-2 text-[10px] text-zinc-400">
        Stages mit Approval-Gate blockieren Story-Transitions, bis die
        Mindestanzahl an Zustimmungen erreicht ist und keine Ablehnung
        offen steht.
      </p>
    </aside>
  );
}
